import { TransformBase } from "./transform_base.js";
import { Transformable } from "./transformable.js";
import { TransformType } from "./transform_base.js";
import { Vector2 } from "../common/vector2.js";
import { ITransformParams } from "../types/transform_params.js";
import type { DomSample } from "../ui/dom_backend/dom_animations.js";

export class TransformCircularMove<T extends Transformable> extends TransformBase
{
  private readonly target: T;
  private readonly center: Vector2;
  private readonly radius: number;
  private readonly start_angle: number;
  private readonly end_angle: number;

  constructor(
    target: T,
    is_important: boolean,
    center: Vector2,
    radius: number,
    anim_start_time: number,
    duration_ms: number,
    start_angle: number = 0,
    end_angle: number = Math.PI * 2,
    params?: ITransformParams
  )
  {
    super( TransformType.Moving, anim_start_time, duration_ms, is_important, params );
    this.target = target;
    this.center = center;
    this.radius = radius;
    this.start_angle = start_angle;
    this.end_angle = end_angle;
  }

  public getTarget(): T
  {
    return this.target;
  }

  protected initImportant(): void
  {
    const initial_offset_x = Math.cos( this.start_angle ) * this.radius;
    const initial_offset_y = Math.sin( this.start_angle ) * this.radius;
    const initial_position = new Vector2( this.center.x + initial_offset_x, this.center.y + initial_offset_y );
    this.target.setPixelPos( initial_position, false );
  }

  protected onUpdateTransform( progress: number ): void
  {
    const current_angle = this.start_angle + ( this.end_angle - this.start_angle ) * progress;
    const offset_x = Math.cos( current_angle ) * this.radius;
    const offset_y = Math.sin( current_angle ) * this.radius;


    if ( this.isImportant() )
    {
      const new_position = new Vector2( this.center.x + offset_x, this.center.y + offset_y );
      this.target.setPixelPos( new_position, false );
    }
    else
    {
      const offset = new Vector2( offset_x, offset_y );
      this.target.addToPixelPos( offset );
    }
  }

  public override domSample( progress: number ): DomSample
  {
    const angle = this.start_angle + ( this.end_angle - this.start_angle ) * progress;
    const offset_x = Math.cos( angle ) * this.radius;
    const offset_y = Math.sin( angle ) * this.radius;
    return this.isImportant()
      ? { pos: new Vector2( this.center.x + offset_x, this.center.y + offset_y ) }
      : { pos: new Vector2( offset_x, offset_y ) };
  }

  public toStrTransform(): string
  {
    return `CircularMove(center: ${this.center.x},${this.center.y} radius: ${this.radius})`;
  }
}